import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';

const COMMENT_PAGE_SIZE = 20;
const FEED_ANON_VIEWER_KEY = 'ch_feed_anon_viewer_id';

const createRandomId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateAnonViewerId = () => {
  try {
    const current = localStorage.getItem(FEED_ANON_VIEWER_KEY);
    if (current) return current;
    const next = createRandomId();
    localStorage.setItem(FEED_ANON_VIEWER_KEY, next);
    return next;
  } catch {
    return createRandomId();
  }
};

const getLevelInfo = (scoreValue) => {
  const thresholds = [0, 100, 300, 600, 1000];
  let level = 1;
  for (let i = thresholds.length - 1; i >= 0; i -= 1) {
    if (scoreValue >= thresholds[i]) {
      level = i + 1;
      break;
    }
  }
  return { level };
};

const FeedDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get('id') || '';
  const { user, role } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [post, setPost] = useState(null);
  const [images, setImages] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [postLikeCount, setPostLikeCount] = useState(0);
  const [postLiked, setPostLiked] = useState(false);
  const [isTogglingPostLike, setIsTogglingPostLike] = useState(false);

  const [comments, setComments] = useState([]);
  const [bestComments, setBestComments] = useState([]);
  const [commentLikeCountById, setCommentLikeCountById] = useState({});
  const [commentLikedById, setCommentLikedById] = useState({});
  const [commentPage, setCommentPage] = useState(1);
  const [commentText, setCommentText] = useState('');
  const [replyTextById, setReplyTextById] = useState({});
  const [editingCommentId, setEditingCommentId] = useState('');
  const [editingText, setEditingText] = useState('');
  const [openReplyParentId, setOpenReplyParentId] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);

  const canEdit = useMemo(() => {
    if (!user?.id || !post?.authorId) return false;
    return user.id === post.authorId || role === 'admin';
  }, [post?.authorId, role, user?.id]);

  const load = useCallback(async () => {
    if (!postId) {
      setError('글 ID가 없습니다.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { data: row, error: postErr } = await supabase
        .from('ch_feed_posts')
        .select('id, title, author_id, author_name, content_html, is_published, created_at, updated_at, view_count')
        .eq('id', postId)
        .maybeSingle();

      if (postErr || !row) {
        setPost(null);
        setImages([]);
        setAuthorProfile(null);
        setError(postErr?.message || '글을 찾을 수 없습니다.');
        return;
      }

      if (!row.is_published && row.author_id !== user?.id && role !== 'admin') {
        setPost(null);
        setImages([]);
        setAuthorProfile(null);
        setError('비공개 글입니다.');
        return;
      }

      const [profileRes, proofRes, postLikeCountRes, myPostLikeRes, commentsRes] = await Promise.all([
        supabase
          .from('ch_profiles')
          .select('id, username, avatar_url, score')
          .eq('id', row.author_id)
          .maybeSingle(),
        supabase
          .from('ch_proofs')
          .select('submitted_at')
          .eq('user_id', row.author_id)
          .eq('is_public', true)
          .order('submitted_at', { ascending: false })
          .limit(1),
        supabase
          .from('ch_feed_post_likes')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', row.id),
        user?.id
          ? supabase
            .from('ch_feed_post_likes')
            .select('id')
            .eq('post_id', row.id)
            .eq('user_id', user.id)
            .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from('ch_feed_comments')
          .select('id, post_id, author_id, author_name, content_text, parent_comment_id, created_at, updated_at')
          .eq('post_id', row.id)
          .order('created_at', { ascending: true }),
      ]);

      const { data: imageRows } = await supabase
        .from('ch_feed_images')
        .select('id, file_path, original_name, created_at')
        .eq('post_id', row.id)
        .order('created_at', { ascending: true });

      const viewerFingerprint = user?.id ? `u:${user.id}` : `g:${getOrCreateAnonViewerId()}`;
      const { data: nextViewCount } = await supabase.rpc('ch_register_feed_post_view', {
        p_post_id: row.id,
        p_fingerprint: viewerFingerprint,
      });

      setPost({
        id: row.id,
        title: row.title || '제목 없음',
        authorId: row.author_id,
        authorName: row.author_name || '익명',
        contentHtml: row.content_html || '',
        viewCount: Number(nextViewCount ?? row.view_count ?? 0),
        createdAt: row.created_at ? String(row.created_at).slice(0, 16).replace('T', ' ') : '',
        updatedAt: row.updated_at ? String(row.updated_at).slice(0, 16).replace('T', ' ') : '',
      });

      setImages(
        (imageRows || []).map((img) => ({
          id: img.id,
          filePath: img.file_path,
          originalName: img.original_name || 'image',
          url: img.file_path ? supabase.storage.from('feed-images').getPublicUrl(img.file_path).data.publicUrl : '',
        }))
      );

      const score = Number(profileRes?.data?.score || 0);
      const levelInfo = getLevelInfo(score);
      const latestProof = proofRes?.data?.[0]?.submitted_at;

      setAuthorProfile({
        id: row.author_id,
        username: profileRes?.data?.username || row.author_name || '익명',
        avatarUrl: profileRes?.data?.avatar_url || '',
        score,
        level: levelInfo.level,
        attendanceDate: latestProof ? String(latestProof).slice(0, 10) : '',
      });

      setPostLikeCount(Number(postLikeCountRes?.count || 0));
      setPostLiked(!!myPostLikeRes?.data?.id);

      const commentRows = commentsRes?.data || [];
      const commentIds = commentRows.map((x) => x.id);
      const [commentLikeCountRes, myCommentLikeRows] = await Promise.all([
        commentIds.length > 0
          ? supabase
            .from('ch_feed_comment_likes')
            .select('comment_id')
            .in('comment_id', commentIds)
          : Promise.resolve({ data: [] }),
        user?.id && commentIds.length > 0
          ? supabase
            .from('ch_feed_comment_likes')
            .select('comment_id')
            .in('comment_id', commentIds)
            .eq('user_id', user.id)
          : Promise.resolve({ data: [] }),
      ]);

      const likeCountMap = {};
      (commentLikeCountRes?.data || []).forEach((x) => {
        if (!x?.comment_id) return;
        likeCountMap[x.comment_id] = (likeCountMap[x.comment_id] || 0) + 1;
      });
      const likedMap = {};
      (myCommentLikeRows?.data || []).forEach((x) => {
        if (!x?.comment_id) return;
        likedMap[x.comment_id] = true;
      });

      const viewComments = commentRows.map((x) => ({
        id: x.id,
        authorId: x.author_id,
        authorName: x.author_name || '익명',
        contentText: x.content_text || '',
        parentCommentId: x.parent_comment_id || '',
        createdAt: x.created_at ? String(x.created_at).slice(0, 16).replace('T', ' ') : '',
        updatedAt: x.updated_at ? String(x.updated_at).slice(0, 16).replace('T', ' ') : '',
      }));

      const rootComments = viewComments.filter((x) => !x.parentCommentId);
      const repliesByParent = viewComments.reduce((acc, cur) => {
        if (!cur.parentCommentId) return acc;
        acc[cur.parentCommentId] = acc[cur.parentCommentId] || [];
        acc[cur.parentCommentId].push(cur);
        return acc;
      }, {});

      setBestComments(
        rootComments
          .filter((x) => Number(likeCountMap[x.id] || 0) >= 5)
          .sort((a, b) => Number(likeCountMap[b.id] || 0) - Number(likeCountMap[a.id] || 0))
          .slice(0, 5)
      );

      setComments(
        rootComments.map((root) => ({
          ...root,
          replies: (repliesByParent[root.id] || []).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
        }))
      );
      setCommentLikeCountById(likeCountMap);
      setCommentLikedById(likedMap);
      setCommentPage(1);
    } finally {
      setIsLoading(false);
    }
  }, [postId, role, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback(async () => {
    if (!canEdit || !post?.id) return;
    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;

    setIsDeleting(true);
    setError('');

    try {
      const { data: imageRows } = await supabase
        .from('ch_feed_images')
        .select('id, file_path')
        .eq('post_id', post.id);

      const paths = (imageRows || []).map((x) => x.file_path).filter(Boolean);
      if (paths.length > 0) {
        await supabase.storage.from('feed-images').remove(paths);
      }

      await supabase.from('ch_feed_images').delete().eq('post_id', post.id);
      const { error: delErr } = await supabase.from('ch_feed_posts').delete().eq('id', post.id);
      if (delErr) {
        setError(delErr.message || '삭제에 실패했습니다.');
        return;
      }

      navigate('/feed');
    } finally {
      setIsDeleting(false);
    }
  }, [canEdit, navigate, post?.id]);

  const ensureLogin = useCallback(() => {
    navigate('/login-registration');
  }, [navigate]);

  const togglePostLike = useCallback(async () => {
    if (!post?.id) return;
    if (!user?.id) {
      ensureLogin();
      return;
    }
    if (isTogglingPostLike) return;
    setIsTogglingPostLike(true);
    try {
      if (!postLiked) {
        const { error: insErr } = await supabase.from('ch_feed_post_likes').insert({ post_id: post.id, user_id: user.id });
        if (!insErr) {
          setPostLiked(true);
          setPostLikeCount((v) => v + 1);
        }
      } else {
        const { error: delErr } = await supabase
          .from('ch_feed_post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);
        if (!delErr) {
          setPostLiked(false);
          setPostLikeCount((v) => Math.max(0, v - 1));
        }
      }
    } finally {
      setIsTogglingPostLike(false);
    }
  }, [ensureLogin, isTogglingPostLike, post?.id, postLiked, user?.id]);

  const toggleCommentLike = useCallback(async (commentId) => {
    if (!commentId) return;
    if (!user?.id) {
      ensureLogin();
      return;
    }
    const liked = !!commentLikedById[commentId];
    if (!liked) {
      const { error: insErr } = await supabase.from('ch_feed_comment_likes').insert({ comment_id: commentId, user_id: user.id });
      if (!insErr) {
        setCommentLikedById((prev) => ({ ...prev, [commentId]: true }));
        setCommentLikeCountById((prev) => ({ ...prev, [commentId]: Number(prev[commentId] || 0) + 1 }));
      }
    } else {
      const { error: delErr } = await supabase
        .from('ch_feed_comment_likes')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.id);
      if (!delErr) {
        setCommentLikedById((prev) => ({ ...prev, [commentId]: false }));
        setCommentLikeCountById((prev) => ({ ...prev, [commentId]: Math.max(0, Number(prev[commentId] || 0) - 1) }));
      }
    }
  }, [commentLikedById, ensureLogin, user?.id]);

  const submitComment = useCallback(async (parentCommentId = '') => {
    if (!post?.id) return;
    if (!user?.id) {
      ensureLogin();
      return;
    }
    const content = parentCommentId ? (replyTextById[parentCommentId] || '').trim() : commentText.trim();
    if (!content) return;
    setIsSavingComment(true);
    try {
      const { error: insErr } = await supabase.from('ch_feed_comments').insert({
        post_id: post.id,
        author_id: user.id,
        author_name: user?.user_metadata?.username || user?.user_metadata?.full_name || user?.email || '익명',
        content_text: content,
        parent_comment_id: parentCommentId || null,
      });
      if (!insErr) {
        if (parentCommentId) {
          setReplyTextById((prev) => ({ ...prev, [parentCommentId]: '' }));
          setOpenReplyParentId('');
        } else {
          setCommentText('');
        }
        await load();
      }
    } finally {
      setIsSavingComment(false);
    }
  }, [commentText, ensureLogin, load, post?.id, replyTextById, user?.email, user?.id, user?.user_metadata?.full_name, user?.user_metadata?.username]);

  const deleteComment = useCallback(async (commentId) => {
    if (!commentId) return;
    if (!user?.id) {
      ensureLogin();
      return;
    }
    const ok = window.confirm('댓글을 삭제하시겠습니까?');
    if (!ok) return;
    const { error: delErr } = await supabase.from('ch_feed_comments').delete().eq('id', commentId);
    if (!delErr) {
      await load();
    }
  }, [ensureLogin, load, user?.id]);

  const saveEditedComment = useCallback(async () => {
    if (!editingCommentId || !editingText.trim()) return;
    if (!user?.id) {
      ensureLogin();
      return;
    }
    const { error: updErr } = await supabase
      .from('ch_feed_comments')
      .update({ content_text: editingText.trim() })
      .eq('id', editingCommentId);
    if (!updErr) {
      setEditingCommentId('');
      setEditingText('');
      await load();
    }
  }, [editingCommentId, editingText, ensureLogin, load, user?.id]);

  const commentRowsWithReplies = useMemo(() => {
    const pages = [];
    let current = [];
    let count = 0;
    (comments || []).forEach((root) => {
      const block = [root, ...(root.replies || []).map((x) => ({ ...x, isReply: true, parentId: root.id }))];
      if (count > 0 && count + block.length > COMMENT_PAGE_SIZE) {
        pages.push(current);
        current = [];
        count = 0;
      }
      current = [...current, ...block];
      count += block.length;
    });
    if (current.length > 0) pages.push(current);
    return pages;
  }, [comments]);

  const totalCommentPages = Math.max(1, commentRowsWithReplies.length || 1);
  const pagedCommentRows = commentRowsWithReplies[Math.min(commentPage, totalCommentPages) - 1] || [];

  const canManageComment = useCallback((authorId) => {
    if (!user?.id || !authorId) return false;
    return user.id === authorId || role === 'admin';
  }, [role, user?.id]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <Seo title={post?.title ? `${post.title} | 피드` : '피드 상세'} canonicalPath="/feed-detail" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : error ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : post ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 border border-border">
            {authorProfile ? (
              <div className="mb-5 rounded-lg border border-border bg-muted/20 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-14 h-14 rounded-full overflow-hidden border border-border bg-muted">
                      <Image src={authorProfile.avatarUrl || '/assets/images/no_image.png'} alt={authorProfile.username} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base md:text-lg font-heading font-semibold text-foreground line-clamp-1">{authorProfile.username}</div>
                      <div className="text-xs text-muted-foreground caption mt-1">
                        출석일: {authorProfile.attendanceDate || '기록 없음'} · 레벨 Lv.{authorProfile.level}
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => navigate(`/people/user?id=${authorProfile.id}`)}>
                    유저 페이지
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">{post.title}</h1>
                <div className="mt-2 text-xs md:text-sm text-muted-foreground caption">
                  {post.createdAt} · {post.authorName}
                </div>
                {post.updatedAt && post.updatedAt !== post.createdAt ? (
                  <div className="mt-1 text-xs text-muted-foreground caption">수정: {post.updatedAt}</div>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                {canEdit ? (
                  <>
                    <Button variant="outline" onClick={() => navigate(`/feed-editor?id=${post.id}`)}>
                      수정
                    </Button>
                    <Button variant="destructive" loading={isDeleting} onClick={handleDelete}>
                      삭제
                    </Button>
                  </>
                ) : null}
              </div>
            </div>

            {(images || []).length > 0 ? (
              <div className="mt-6 overflow-x-auto">
                <div className="flex flex-nowrap gap-3 min-w-max pb-1">
                {images.map((img) => (
                  <a
                    key={img.id}
                    href={img.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-shrink-0 rounded-md border border-border bg-muted overflow-hidden p-2"
                  >
                    <img src={img.url} alt={img.originalName} className="w-auto h-auto max-h-[28rem] object-contain" />
                  </a>
                ))}
                </div>
              </div>
            ) : null}

            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={togglePostLike}
                disabled={isTogglingPostLike}
                className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm transition-gaming ${postLiked ? 'bg-destructive text-destructive-foreground border-destructive' : 'bg-muted/40 text-foreground border-border hover:bg-muted'}`}
              >
                <Icon name="Heart" size={16} color="currentColor" className={postLiked ? 'fill-current' : ''} />
                <span>좋아요</span>
                <span className="font-semibold tabular-nums">{postLikeCount}</span>
              </button>
            </div>

            <div className="mt-6 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: post.contentHtml }} />

            <div className="mt-8 pt-6 border-t border-border space-y-5">
              <div>
                <h2 className="text-base md:text-lg font-heading font-semibold text-foreground">베스트 댓글</h2>
                {(bestComments || []).length === 0 ? (
                  <div className="mt-2 text-xs text-muted-foreground caption">추천 5개 이상 댓글이 없습니다.</div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {bestComments.map((c) => (
                      <div key={`best-${c.id}`} className="rounded-md border border-border bg-muted/20 p-3">
                        <div className="text-sm font-medium text-foreground">{c.authorName}</div>
                        <div className="text-xs text-muted-foreground caption mt-1">{c.createdAt}</div>
                        <div className="mt-2 text-sm text-foreground whitespace-pre-line">{c.contentText}</div>
                        <div className="mt-2 text-xs text-primary">추천 {Number(commentLikeCountById[c.id] || 0)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-base md:text-lg font-heading font-semibold text-foreground">댓글</h2>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e?.target?.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="댓글을 입력하세요"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button variant="default" onClick={() => submitComment('')} loading={isSavingComment}>
                      댓글 등록
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {pagedCommentRows.length === 0 ? (
                  <div className="text-sm text-muted-foreground caption">등록된 댓글이 없습니다.</div>
                ) : (
                  pagedCommentRows.map((c) => (
                    <div key={c.id} className={`rounded-md border border-border p-3 ${c.isReply ? 'ml-4 bg-muted/10' : 'bg-card'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-medium text-foreground">{c.authorName}</div>
                          <div className="text-xs text-muted-foreground caption mt-1">{c.createdAt}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleCommentLike(c.id)}
                            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs border ${commentLikedById[c.id] ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 text-foreground border-border'}`}
                          >
                            추천 {Number(commentLikeCountById[c.id] || 0)}
                          </button>
                          {!c.isReply ? (
                            <button
                              type="button"
                              onClick={() => setOpenReplyParentId((v) => (v === c.id ? '' : c.id))}
                              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs border bg-muted/30 text-foreground border-border"
                            >
                              답글
                            </button>
                          ) : null}
                        </div>
                      </div>

                      {editingCommentId === c.id ? (
                        <div className="mt-2 space-y-2">
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e?.target?.value)}
                            rows={3}
                            maxLength={1000}
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="outline" onClick={() => { setEditingCommentId(''); setEditingText(''); }}>
                              취소
                            </Button>
                            <Button variant="default" onClick={saveEditedComment}>
                              저장
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-foreground whitespace-pre-line">{c.contentText}</div>
                      )}

                      {canManageComment(c.authorId) && editingCommentId !== c.id ? (
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => { setEditingCommentId(c.id); setEditingText(c.contentText || ''); }}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteComment(c.id)}
                            className="text-xs text-destructive hover:underline"
                          >
                            삭제
                          </button>
                        </div>
                      ) : null}

                      {openReplyParentId === c.id ? (
                        <div className="mt-3 rounded-md border border-border bg-muted/10 p-2">
                          <textarea
                            value={replyTextById[c.id] || ''}
                            onChange={(e) => setReplyTextById((prev) => ({ ...prev, [c.id]: e?.target?.value }))}
                            rows={2}
                            maxLength={1000}
                            placeholder="대댓글을 입력하세요"
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                          />
                          <div className="mt-2 flex justify-end">
                            <Button variant="default" onClick={() => submitComment(c.id)} loading={isSavingComment}>
                              대댓글 등록
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setCommentPage((v) => Math.max(1, v - 1))}
                  disabled={commentPage <= 1}
                  className="px-3 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50"
                >
                  이전
                </button>
                <div className="text-xs text-muted-foreground caption">댓글 페이지 {commentPage} / {totalCommentPages}</div>
                <button
                  type="button"
                  onClick={() => setCommentPage((v) => Math.min(totalCommentPages, v + 1))}
                  disabled={commentPage >= totalCommentPages}
                  className="px-3 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50"
                >
                  다음
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default FeedDetail;
